import { Dialog } from '@headlessui/react';
import { X, Plus, Minus, ChevronDown, ChevronUp, ShoppingCart } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '../../../../components/ui/Button';
import { Product } from '../../types';
import { cn } from '../../../../utils/cn';

interface OrderItem {
  product: Product;
  quantity: number;
}

interface UnifiedOrderModalProps {
  initialProduct: Product;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (orderData: any) => void;
  initialRequestType: 'leasing' | 'location' | 'achat' | 'cash';
}

export function UnifiedOrderModal({ 
  initialProduct, 
  isOpen, 
  onClose, 
  onSubmit,
  initialRequestType = 'leasing'
}: UnifiedOrderModalProps) {
  // État des produits dans le panier
  const [orderItems, setOrderItems] = useState<OrderItem[]>([
    { product: initialProduct, quantity: 1 }
  ]);
  
  // État du type de paiement sélectionné
  const [paymentType, setPaymentType] = useState<'leasing' | 'location' | 'achat' | 'cash'>(initialRequestType);
  
  // Données du formulaire
  const [formData, setFormData] = useState({
    leasingCode: '',
    duration: '',
    financingType: '',
    deliveryAddress: '',
    message: '',
    downPayment: 0,
    installmentPeriod: '',
    installmentCount: 12
  });

  // Erreurs de validation
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  // État pour afficher/masquer les produits similaires
  const [showSimilarProducts, setShowSimilarProducts] = useState(false);
  
  // Produits similaires (simulés pour l'exemple)
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);

  // Récupérer les produits similaires
  useEffect(() => {
    // Simulation d'une API ou d'une fonction pour récupérer les produits similaires
    // Dans une implémentation réelle, vous feriez un appel API ou utiliseriez un hook
    const fetchSimilarProducts = async () => {
      // Simulez quelques produits similaires basés sur le produit initial
      const mockSimilarProducts = [
        {
          ...initialProduct,
          id: 'similar1',
          name: `${initialProduct.name} (Version basique)`,
          price: Math.round(initialProduct.price * 0.8),
          monthlyPayment: Math.round(initialProduct.monthlyPayment * 0.8)
        },
        {
          ...initialProduct,
          id: 'similar2',
          name: `${initialProduct.name} (Version premium)`,
          price: Math.round(initialProduct.price * 1.2),
          monthlyPayment: Math.round(initialProduct.monthlyPayment * 1.2)
        },
      ];
      
      setSimilarProducts(mockSimilarProducts);
    };
    
    if (isOpen) {
      fetchSimilarProducts();
    }
  }, [isOpen, initialProduct]);

  // Calcul des totaux
  const calculateTotals = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    const totalMonthlyPayment = orderItems.reduce((sum, item) => sum + (item.product.monthlyPayment * item.quantity), 0);
    
    // Calcul du montant d'acompte
    const downPayment = formData.downPayment || 0;
    
    // Calcul du montant mensuel après acompte (pour le mode achat échelonné)
    const remainingAmount = subtotal - downPayment;
    const installmentCount = formData.installmentCount || 12;
    const monthlyInstallment = remainingAmount / installmentCount;
    
    return {
      subtotal,
      totalMonthlyPayment,
      downPayment,
      remainingAmount,
      monthlyInstallment
    };
  };

  const totals = calculateTotals();

  // Gérer l'ajout d'un produit
  const addProductToOrder = (product: Product) => {
    const existingItemIndex = orderItems.findIndex(item => item.product.id === product.id);
    
    if (existingItemIndex >= 0) {
      // Produit déjà dans le panier, augmenter la quantité
      const updatedItems = [...orderItems];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + 1
      };
      setOrderItems(updatedItems);
    } else {
      // Nouveau produit, ajouter au panier
      setOrderItems([...orderItems, { product, quantity: 1 }]);
    }
  };

  // Gérer la suppression d'un produit
  const removeProductFromOrder = (productId: string) => {
    setOrderItems(orderItems.filter(item => item.product.id !== productId));
  };

  // Gérer la modification de la quantité
  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    const updatedItems = orderItems.map(item => 
      item.product.id === productId ? { ...item, quantity: newQuantity } : item
    );
    
    setOrderItems(updatedItems);
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (paymentType === 'leasing' && (!formData.leasingCode || !/^WL-\w{8}$/.test(formData.leasingCode))) {
      newErrors.leasingCode = 'Veuillez saisir un code de leasing valide (WL-XXXXXXXX).';
    }
    
    if (!formData.duration) {
      newErrors.duration = 'Veuillez sélectionner une durée.';
    }
    
    if (!formData.deliveryAddress) {
      newErrors.deliveryAddress = 'Veuillez saisir une adresse de livraison.';
    }
    
    if (paymentType === 'achat' && formData.downPayment < 0) {
      newErrors.downPayment = 'L\'acompte ne peut pas être négatif.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Créer l'objet de commande
    const orderData = {
      paymentType,
      formData,
      orderItems,
      totals: calculateTotals()
    };
    
    onSubmit(orderData);
  };

  // Réinitialisation du formulaire à la fermeture
  const handleClose = () => {
    setErrors({});
    onClose();
  };

  // Titre dynamique en fonction du mode de paiement
  const getModalTitle = () => {
    switch (paymentType) {
      case 'leasing':
        return 'Commander en leasing';
      case 'location':
        return 'Commander en location';
      case 'achat':
        return 'Commander avec paiement échelonné';
      case 'cash':
        return 'Commander avec paiement comptant';
      default:
        return 'Commander';
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto w-full max-w-3xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {getModalTitle()}
            </Dialog.Title>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Colonne 1: Panier et produits */}
              <div className="md:col-span-2 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Votre commande
                  </h3>
                  
                  <div className="space-y-3">
                    {orderItems.map((item) => (
                      <div key={item.product.id} className="flex items-center justify-between bg-white p-3 rounded border border-gray-100">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={item.product.image} 
                            alt={item.product.name} 
                            className="h-12 w-12 object-cover rounded"
                          />
                          <div>
                            <p className="text-sm font-medium">{item.product.name}</p>
                            <p className="text-xs text-gray-500">${item.product.price.toLocaleString('en-US')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button 
                            type="button"
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button 
                            type="button"
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          
                          {orderItems.length > 1 && (
                            <button 
                              type="button"
                              onClick={() => removeProductFromOrder(item.product.id)}
                              className="ml-2 p-1 text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => setShowSimilarProducts(!showSimilarProducts)}
                    className="flex items-center text-primary hover:text-primary-hover mt-3 text-sm"
                  >
                    {showSimilarProducts ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Masquer les produits complémentaires
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        Voir les produits complémentaires
                      </>
                    )}
                  </button>
                  
                  {showSimilarProducts && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {similarProducts.map((product) => (
                        <div 
                          key={product.id} 
                          className="bg-white p-3 rounded border border-gray-100 flex items-center justify-between"
                        >
                          <div className="flex items-center space-x-2">
                            <img 
                              src={product.image} 
                              alt={product.name} 
                              className="h-10 w-10 object-cover rounded"
                            />
                            <div>
                              <p className="text-xs font-medium">{product.name}</p>
                              <p className="text-xs text-gray-500">${product.price.toLocaleString('en-US')}</p>
                            </div>
                          </div>
                          <Button 
                            type="button"
                            variant="secondary" 
                            size="sm"
                            onClick={() => addProductToOrder(product)}
                            className="text-xs"
                          >
                            Ajouter
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Options de paiement */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3">Mode de paiement</h3>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {['leasing', 'location', 'achat', 'cash'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={cn(
                          "p-3 border rounded-lg text-center text-sm transition-all",
                          paymentType === type 
                            ? "border-primary-500 bg-primary-50 text-primary-700 font-medium" 
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        )}
                        onClick={() => setPaymentType(type as any)}
                      >
                        {type === 'leasing' && 'Leasing'}
                        {type === 'location' && 'Location'}
                        {type === 'achat' && 'Échelonné'}
                        {type === 'cash' && 'Comptant'}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Champs de formulaire dynamiques selon le mode de paiement */}
                <div className="space-y-4">
                  {paymentType === 'leasing' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Code de leasing <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          value={formData.leasingCode}
                          onChange={(e) => setFormData({ ...formData, leasingCode: e.target.value.toUpperCase() })}
                          className={cn(
                            "block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary font-mono",
                            errors.leasingCode && "border-red-300 focus:border-red-500 focus:ring-red-500"
                          )}
                          placeholder="WL-XXXXXXXX"
                          maxLength={11}
                        />
                        {errors.leasingCode && (
                          <p className="mt-1 text-red-600 text-xs">{errors.leasingCode}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Le code de leasing est généré dans l'app mobile ou l'app Financement PME.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Durée {paymentType === 'leasing' ? 'du leasing' : 
                            paymentType === 'location' ? 'de location' : 
                            paymentType === 'achat' ? 'de paiement' : ''}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className={cn(
                        "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary",
                        errors.duration && "border-red-300"
                      )}
                    >
                      <option value="">Sélectionnez une durée</option>
                      {initialProduct.availableDurations?.map((duration) => (
                        <option key={duration} value={duration}>
                          {duration} mois
                        </option>
                      ))}
                    </select>
                    {errors.duration && (
                      <p className="mt-1 text-red-600 text-xs">{errors.duration}</p>
                    )}
                  </div>
                  
                  {/* Champs spécifiques pour le paiement échelonné */}
                  {paymentType === 'achat' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Montant d'acompte
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={formData.downPayment}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            downPayment: parseFloat(e.target.value) || 0
                          })}
                          className={cn(
                            "block w-full pl-7 rounded-md border-gray-300 focus:border-primary focus:ring-primary",
                            errors.downPayment && "border-red-300"
                          )}
                        />
                      </div>
                      {errors.downPayment && (
                        <p className="mt-1 text-red-600 text-xs">{errors.downPayment}</p>
                      )}
                      
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Nombre de mensualités
                        </label>
                        <select
                          value={formData.installmentCount}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            installmentCount: parseInt(e.target.value) 
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                        >
                          <option value="3">3 mois</option>
                          <option value="6">6 mois</option>
                          <option value="12">12 mois</option>
                          <option value="18">18 mois</option>
                          <option value="24">24 mois</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Adresse de livraison <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.deliveryAddress}
                      onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                      className={cn(
                        "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary",
                        errors.deliveryAddress && "border-red-300"
                      )}
                      placeholder="Adresse complète de livraison"
                    />
                    {errors.deliveryAddress && (
                      <p className="mt-1 text-red-600 text-xs">{errors.deliveryAddress}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Message (optionnel)
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
              
              {/* Colonne 2: Récapitulatif et total */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3">Récapitulatif</h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sous-total:</span>
                      <span>${totals.subtotal.toLocaleString('en-US')}</span>
                    </div>
                    
                    {paymentType === 'leasing' && (
                      <div className="flex justify-between font-medium">
                        <span>Mensualité leasing:</span>
                        <span>${totals.totalMonthlyPayment.toLocaleString('en-US')}/mois</span>
                      </div>
                    )}
                    
                    {paymentType === 'location' && (
                      <div className="flex justify-between font-medium">
                        <span>Mensualité location:</span>
                        <span>${totals.totalMonthlyPayment.toLocaleString('en-US')}/mois</span>
                      </div>
                    )}
                    
                    {paymentType === 'achat' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Acompte:</span>
                          <span>${formData.downPayment.toLocaleString('en-US')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Solde restant:</span>
                          <span>${totals.remainingAmount.toLocaleString('en-US')}</span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span>Mensualité:</span>
                          <span>${totals.monthlyInstallment.toLocaleString('en-US')}/mois</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Sur {formData.installmentCount} mois</span>
                        </div>
                      </>
                    )}
                    
                    {paymentType === 'cash' && (
                      <div className="flex justify-between font-medium">
                        <span>Montant total:</span>
                        <span>${totals.subtotal.toLocaleString('en-US')}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full justify-center"
                >
                  Commander maintenant
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full justify-center"
                  onClick={handleClose}
                >
                  Annuler
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
